
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Leaf, ChevronRight } from 'lucide-react';

const Navbar: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <nav className={`fixed w-full z-[100] transition-all duration-500 ${
      scrolled ? 'bg-white/90 backdrop-blur-xl shadow-lg py-3' : 'bg-transparent py-5'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3 group">
            <div className="bg-teal-600 p-2 rounded-xl group-hover:rotate-12 transition-transform duration-500 shadow-lg shadow-teal-600/20">
              <Leaf className="h-6 w-6 text-white" />
            </div>
            <span className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight group-hover:text-teal-600 transition-colors">
              Sangeeta Wellness
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center space-x-10">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`relative py-1 text-sm font-bold tracking-tight transition-all duration-300 group ${
                  isActive(link.path) ? 'text-teal-600' : 'text-slate-600 hover:text-teal-600'
                }`}
              >
                {link.name}
                <span className={`absolute -bottom-1 left-0 h-0.5 bg-teal-600 transition-all duration-500 ${
                  isActive(link.path) ? 'w-full' : 'w-0 group-hover:w-full'
                }`}></span>
              </Link>
            ))}
            <Link
              to="/contact"
              className="bg-slate-900 text-white px-7 py-3 rounded-2xl text-sm font-bold hover:bg-teal-600 transition-all shadow-xl shadow-slate-900/10 active:scale-95"
            >
              Get in Touch
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="p-2 text-slate-900 bg-white shadow-sm rounded-xl border border-slate-100"
              aria-label="Toggle menu"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav Overlay */}
      <div className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-[-1] transition-opacity duration-500 md:hidden ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`} onClick={() => setIsOpen(false)}></div>

      <div className={`absolute top-full left-0 w-full bg-white shadow-2xl transition-all duration-500 md:hidden overflow-hidden ${
        isOpen ? 'max-h-[500px] border-b border-slate-100' : 'max-h-0'
      }`}>
        <div className="px-6 py-10 space-y-4">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.path}
              onClick={() => setIsOpen(false)}
              className={`flex items-center justify-between px-6 py-4 rounded-2xl text-lg font-bold transition-all ${
                isActive(link.path) 
                  ? 'bg-teal-50 text-teal-600' 
                  : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              {link.name}
              <ChevronRight className={`w-5 h-5 transition-transform ${isActive(link.path) ? 'translate-x-1' : ''}`} />
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={() => setIsOpen(false)}
            className="block w-full text-center mt-8 bg-teal-600 text-white py-5 rounded-3xl font-bold shadow-2xl shadow-teal-600/20"
          >
            Get in Touch
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
